import * as React from 'react';
import * as _ from 'lodash';
import { connect, ConnectedProps } from 'react-redux';
import {
    rememberProjectApiArguments,
    rememberProjectApiResult,
    getProjectApiHistory,
    storeProjectApiHistory
} from '../../../../redux/clientCodeRunner/actions.clientCodeRunner';
import { IStore } from '../../../../redux/initialState';
import LeftDebugChatPanel from '../LeftDebugChatPanel/LeftDebugChatPanel';
import OutsideClickPanel from '../../../components/OutsideClickPanel/OutsideClickPanel';
import ComboBox from '../../../components/ComboBox/ComboBox';
import TextFieldWithJsonWrapper from '../../../components/TextFieldWithJsonWrapper/TextFieldWithJsonWrapper';
import JsonPanelHeader from '../JsonPanel/JsonPanelHeader';
import FilterSwitcher from '../../../components/FilterSwitcher/FilterSwitcher';
import PopupButton from '../../../components/Popup/PopupButton';
import PopupButtons from '../../../components/Popup/PopupButtons';
import JsonPanelBody from '../JsonPanel/JsonPanelBody';
import JsonPanel from '../JsonPanel/JsonPanel';
import menuItems from '../../../../const/menuItems';
import { prepareJsonString } from '../../../../services/mini/prepareJsonString';
import asyncDispatch from '../../../../services/mini/asyncDispatch';
import { Loader, LoaderSize, LoaderTheme } from '../../../components/Loader/Loader';
import { Resizer } from '../../../components/Resizer/Resizer';
import {
    IRememberProjectApiArgumentsParams,
    IRememberProjectApiResultParams,
    IGetProjectApiHistoryParams,
    IStoreProjectApiHistoryParams
} from '../../../../redux/clientCodeRunner/saga.clientCodeRunner';
import animateTop from '../../../../services/mini/animateTop';
import { IProjectApiCallInfo } from '../../../../models/IProjectApiCallInfo';
import formats from '../../../../const/formats';
import * as moment from 'moment';
import CODE_MIRROR_OPTIONS from '../../../../const/codeMirrorOptions';
import ToasterService from '../../../../services/ToasterService';
import ReactJson from 'react-json-view';
import { isJSONString } from '../../../../services/mini/isJSONString';
import { getEnvironmentValue } from '../../../../services/mini/getEnvironmentValue';
import { useCallback, useEffect, useRef, useState } from 'react';
import copyJSONToBuffer from '../../../../services/mini/copyJSONToBuffer';
import { JS_CM_TRANSFORM_MODE } from '../TransformMode/cm_transform_modes';
import '../../../../services/cm/transformsKeywords';
import { copyTextToBuffer } from '../../../../../../alan_btn/src/helpers/mini/copyTextToBuffer';
import { smartJSONParse } from '../../../../services/mini/isValidJSON';
import isLocalStorageAvailable from '../../../../services/mini/isLocalStorageAvailable';
import { Environment } from '../../../../const/environment';
import { guid } from '../../../../../../alan_btn/src/helpers/mini/guid';

type CallProjectApiPanelMode = 'run-script' | 'run-fn';

const SHOW_ALL_CALLS_FILTER_KEY = 'show-all-calls-filter';

const isRunFnMode = (mode: CallProjectApiPanelMode): mode is 'run-fn' => {
    return mode === 'run-fn';
};

function returnPretifiedResult(result) {
    try {
        return JSON.stringify(result);
    } catch (error) {

    }
    return null;
}


type CallProjectApiPanelOwnProps = {
    projectId: string,
    environment: Environment,
    historyKey: string,
    isOpened: boolean,
    mode: CallProjectApiPanelMode,
    text?: string,
    transformName?: string,
    selectedProjectApi?: string,
    onSelectProjectApi?: (projectApiKey: string) => void,
    onClose: () => void,
};

function CallProjectApiPanel({
    projectId,
    environment,
    projectApis,
    transformName,
    selectedProjectApi,
    paramsHistory,
    isProjectConnected,
    mode,
    text,
    isOpened,
    historyKey,
    rememberProjectApiArguments,
    rememberProjectApiResult,
    getProjectApiHistory,
    onSelectProjectApi,
    onClose,

}: PropsFromRedux & CallProjectApiPanelOwnProps) {

    const projectApiName = isRunFnMode(mode) ? projectApis[selectedProjectApi]?.name : transformName;

    const [historyLoading, setHistoryLoading] = useState(true);
    const [opened, setOpened] = useState(false);
    const [paramVal, setParamVal] = useState(isRunFnMode(mode) ? '{}' : text);
    const [currentParamInd, setCurrentParamInd] = useState(null);
    const [isParamValid, setIsParamValid] = useState(true);
    const [callApiTs, setCallApiTs] = useState(0);
    const [callError, setCallError] = useState(null);
    const [callResult, setCallResult] = useState(null);
    const [isCallInProcess, setIsCallInProcess] = useState(false);
    const currentCallId = useRef({});

    let savedShowAllCallsVal = false;

    if (isLocalStorageAvailable() && localStorage.getItem(SHOW_ALL_CALLS_FILTER_KEY)) {
        savedShowAllCallsVal = localStorage.getItem(SHOW_ALL_CALLS_FILTER_KEY) === 'true';
    }

    const [showAllCalls, setShowAllCalls] = useState(savedShowAllCallsVal);

    const listRef = useRef<HTMLDivElement>();

    const paramsHistoryFiltered = ((showAllCalls ?
        [...paramsHistory] :
        (paramsHistory
            .filter(historyItem => historyItem.projectApiName === projectApiName))))
        .sort((a, b) => b.callTs - a.callTs);

    useEffect(() => {
        setOpened(isOpened);
        getProjectApiHistory({
            projectId: projectId,
            environment: getEnvironmentValue(environment),
            projectApiName: projectApiName,
            historyKey
        }).then(() => {
            setHistoryLoading(false);
        });
    }, [isOpened, getProjectApiHistory, projectId, environment, projectApiName, historyKey]);

    useEffect(() => {
        if (callApiTs) {
            animateTop(listRef.current);
            setCurrentParamInd(0);
        }
    }, [callApiTs]);

    const value = paramVal || '';

    const callApiCb = useCallback(() => {
        const waitingLabel = 'Waiting for result...';
        if (!isParamValid) return;

        if (isRunFnMode(mode)) {
            if (!window.alanBtnInstance || !isProjectConnected) {
                ToasterService.showInfo({ title: 'Project is connecting. Please wait and try again later.' });
                return;
            }
        }

        setIsCallInProcess(true);
        setTimeout(() => {
            setIsCallInProcess(false);
        }, 60000);

        const callId = guid();
        currentCallId.current = callId;
        if (isRunFnMode(mode)) {
            const param = paramVal ? smartJSONParse(prepareJsonString(paramVal)) : undefined;
            rememberProjectApiArguments({
                projectId,
                param,
                functionName: projectApiName,
                projectApiName: projectApiName,
                result: waitingLabel,
                historyKey,
                environment,
                callId
            });
            setCallError(null);
            setCallResult(waitingLabel);
            window.alanBtnInstance.callProjectApi(projectApiName, param, (error, result) => {
                setTimeout(() => {
                    rememberProjectApiResult({
                        projectId,
                        error: error ? JSON.stringify(error) : null,
                        result: returnPretifiedResult(result),
                        environment,
                        historyKey,
                        callId
                    });
                    if (currentCallId?.current === callId) {
                        setCallError(error ? JSON.stringify(error) : null);
                        setCallResult(returnPretifiedResult(result));
                    }
                    setIsCallInProcess(false);
                }, 1000);
            });
        } else {
            const param = paramVal || '';

            setCallError(null);
            setCallResult(waitingLabel);
            window.tutorProject.call('runScript', {
                script: param,
            }, function (error, result) {
                console.log(error, result);
                setTimeout(() => {
                    rememberProjectApiArguments({
                        projectId,
                        param,
                        projectApiName: projectApiName,
                        result: waitingLabel,
                        functionName: result?.function,
                        historyKey,
                        environment,
                        callId
                    });

                    if (result.status === 'ok') {
                        rememberProjectApiResult({
                            projectId,
                            error: error ? JSON.stringify(error) : null,
                            result: JSON.stringify(result?.result),
                            environment,
                            historyKey,
                            callId
                        });
                        if (currentCallId?.current === callId) {
                            setCallError(null);
                            setCallResult(JSON.stringify(result?.result));
                        }
                    } else {
                        setCallError(result?.message);
                        setCallResult(null);
                    }
                    setIsCallInProcess(false);
                }, 1000);
            });
        }

        setCallApiTs(Date.now());
    }, [isParamValid, isProjectConnected, mode, paramVal, paramsHistory.length, projectApiName, projectId, rememberProjectApiArguments, rememberProjectApiResult]);


    useEffect(() => {
        if (!isRunFnMode(mode)) {
            callApiCb();
        }
    }, []);// do not chnage it - this should be run only once



    const onValidateCb = useCallback((validationResult: boolean) => {
        setIsParamValid(validationResult);
    }, []);

    const onChangeCb = useCallback((val: string) => {
        setParamVal(val);
    }, []);

    const selectHistoryItemCb = useCallback((index: number) => {
        if (!paramsHistoryFiltered[index]) return;
        currentCallId.current = paramsHistoryFiltered[index]?.callId;
        setCurrentParamInd(index);
        if (isRunFnMode(mode)) {
            setParamVal(JSON.stringify(paramsHistoryFiltered[index].param, null, CODE_MIRROR_OPTIONS.JSON_SPACE));
        } else {
            setParamVal(paramsHistoryFiltered[index].param as unknown as string);
        }

        setCallError(paramsHistoryFiltered[index].error);
        setCallResult(paramsHistoryFiltered[index].result);

        const projectApiKeys = Object.keys(projectApis);
        for (let i = 0; i < projectApiKeys.length; i++) {
            const curKey = projectApiKeys[i];
            if (projectApis[curKey].name === paramsHistoryFiltered[index].projectApiName) {
                onSelectProjectApi(curKey);
                break;
            }
        }
    }, [mode, onSelectProjectApi, paramsHistoryFiltered, projectApis]);

    const clearCb = useCallback(() => {
        setParamVal('');
    }, []);

    const onSelectProjectApiCb = useCallback((projectApiInfo) => {
        onSelectProjectApi(projectApiInfo.key);
        setCallError(null);
        setCallResult(null);
    }, [onSelectProjectApi]);

    const copyCb = useCallback(() => {
        const valToCopy = callResult || callError;

        copyJSONToBuffer(valToCopy);

        ToasterService.showInfo({
            title: 'Copied to clipboard'
        });
    }, [callError, callResult]);

    const copyParamCb = useCallback(() => {
        copyTextToBuffer(paramVal);

        ToasterService.showInfo({
            title: 'Copied to clipboard'
        });
    }, [paramVal]);

    const allProjectApis = Object.keys(projectApis).map(key => {
        return {
            key: key,
            name: projectApis[key].name,
        };
    });

    const getLastChangeText = (ts) => {
        return moment(ts).format(formats.MM_DD_YY_hh_mm_a);
    };

    const onResizeStart = useCallback(() => {
    }, []);

    const onResizeEnd = useCallback(({ height }) => {
        return true;
    }, []);

    const onStateChange = useCallback(() => {
        return true;
    }, []);

    const resizerRef = useRef();

    return <LeftDebugChatPanel opened={opened} className={isRunFnMode(mode) ? '' : 'full-mode'}>
        <OutsideClickPanel
            onOutsideClick={onClose}
            opened={opened}
            classes="outside-click-panel full-width">
            <div className={`call-project-api-panel full-mode ${isRunFnMode(mode) ? '' : 'full-mode'}`}>
                <div className="call-project-api-panel__list">
                    <div className={`call-project-api-panel__list-header with-filter`}>
                        History
                        <FilterSwitcher
                            labelIfTrue='Show all'
                            labelIfFalse='Show all'
                            offMode={true}
                            minWidth="100px"
                            maxWidth="100px"
                            labelOnLeftSide={true}
                            onClick={() => {
                                const newVal = !showAllCalls;
                                if (isLocalStorageAvailable()) {
                                    localStorage.setItem(SHOW_ALL_CALLS_FILTER_KEY, newVal.toString());
                                }
                                setShowAllCalls(newVal);
                                selectHistoryItemCb(0);
                            }}
                            value={showAllCalls} />
                    </div>
                    <div className={`call-project-api-panel__list-body`} ref={listRef}>
                        {historyLoading && <Loader theme={LoaderTheme.Light} size={LoaderSize.Medium} />}
                        {!historyLoading && paramsHistoryFiltered?.length > 0 &&
                            paramsHistoryFiltered.map((histEl: IProjectApiCallInfo, index: number) => {
                                return <div
                                    className={`call-project-api-panel__list-item ${index === currentParamInd && 'selected'}`}
                                    onClick={() => selectHistoryItemCb(index)}
                                    key={index}>
                                    <div className="call-project-api-panel__list-item-name">
                                        {isRunFnMode(mode) ?
                                            `${histEl.projectApiName} ${histEl.param ? `(${JSON.stringify(histEl.param)})` : ''}` :
                                            `${(histEl as any).functionName || histEl.param || ''}`
                                        }
                                    </div>
                                    <div className="call-project-api-panel__list-item-ts">
                                        {getLastChangeText(histEl.callTs)}
                                    </div>
                                </div>;
                            })
                        }
                        {!historyLoading && paramsHistoryFiltered?.length < 1 && <div className="call-project-api-panel__no-history">History is empty</div>}
                    </div>
                </div>
                <JsonPanel className="call-project-api-panel__json">
                    <JsonPanelHeader
                        content={
                            isRunFnMode(mode) ?
                                <React.Fragment>
                                    <div>Invoke:</div>
                                    <ComboBox
                                        classes={`call-project-api-panel__cb`}
                                        width="200px"
                                        values={allProjectApis}
                                        valueKey="key"
                                        valueName="name"
                                        selectedValueId={selectedProjectApi}
                                        renderItem={(item) => {
                                            return <div className="call-project-api-panel__cb-item">{item.name}</div>;
                                        }}
                                        onValueSelect={onSelectProjectApiCb}
                                    />
                                </React.Fragment> :
                                <div>Invoke</div>
                        }
                        title={`Call: ${projectApiName}`}
                        docUrl={isRunFnMode(mode) ? `${menuItems.docs.href}/client-api/methods/common-api/#callprojectapi` : null}
                        value={value}
                        showBtns={false}
                        onClear={clearCb}
                        onClose={onClose}
                    />
                    <JsonPanelBody>
                        <div className="call-project-api-panel__wrapper">
                            <div className="call-project-api-panel__params-wrapper">
                                <div className="call-project-api-panel__panel-label">{isRunFnMode(mode) ? 'Parameters:' : 'Script:'} </div>

                                <div className={`call-project-api-panel__params-btns ${!value && 'disabled'}`}>
                                    <div className={`call-project-api-panel__params-btns__clear-btn action-btn light-blue-with-border`} onClick={() => { setParamVal(''); }}>Clear</div>
                                    <div className="call-project-api-panel__params-btns__copy-btn action-btn light-blue-with-border" onClick={copyParamCb}>Copy</div>
                                </div>

                                {opened &&
                                    <TextFieldWithJsonWrapper
                                        key={currentParamInd}
                                        value={value}
                                        withCursorInsideBrackets={value === '{}'}
                                        mode={!isRunFnMode(mode) ? JS_CM_TRANSFORM_MODE : null}
                                        placeholder={<div>
                                            Enter Parameters, for example:
                                            <br />
                                            <br />
                                            <div>
                                                {'{'} <br />
                                                {'    field1: ...,'} <br />
                                                {'    field2: ...,'} <br />
                                                {'}'} <br />
                                            </div>
                                        </div>}
                                        onChange={onChangeCb}
                                        onValidate={onValidateCb}
                                        onShiftEnter={callApiCb}
                                        skipValidation={!isRunFnMode(mode)}
                                    />}
                            </div>

                            <Resizer
                                lsKey={'CallProjectApiResizerRoot'}
                                ref={resizerRef}
                                x={100}
                                y={50}
                                min={30}
                                max={70}
                                isOpened={true}
                                onResizeEnd={onResizeEnd}
                                onStateChange={onStateChange}
                                onResizeStart={onResizeStart}
                                styleName={'call-project-api__resizer'}
                                divider="top">
                                <div className="call-project-api-panel__result-wrapper">

                                    <div className="call-project-api-panel__panel-label">Result:</div>
                                    <div className="call-project-api-panel__call-result-panel">
                                        <div className={`call-project-api-panel__copy-res-btn action-btn light-blue-with-border ${((callResult || callError) && !isCallInProcess) ? '' : 'disabled'}`} onClick={copyCb}>Copy</div>
                                        {callResult &&
                                            <div>
                                                {
                                                    isJSONString(callResult)
                                                        ?
                                                        <div className="call-project-api-panel__call-json-result">
                                                            <ReactJson
                                                                src={JSON.parse(callResult)}
                                                                name={null}
                                                                theme="rjv-default"
                                                                iconStyle="square"
                                                                displayDataTypes={false}
                                                                displayObjectSize={true}
                                                                enableClipboard={false}
                                                                collapsed={false}
                                                            />
                                                        </div>
                                                        :
                                                        <div className="call-project-api-panel__call-text-result">{callResult}</div>
                                                }
                                            </div>
                                        }
                                        {callError && <div className="call-project-api-panel__call-result-err">
                                            {`Error: ${callError}`}
                                        </div>}
                                    </div>

                                </div>
                            </Resizer>

                        </div>

                    </JsonPanelBody>
                    <PopupButtons>
                        <PopupButton
                            label={isRunFnMode(mode) ? "Call" : "Run"}
                            action={callApiCb}
                            className={`${!isParamValid && 'disabled'} ${isCallInProcess && 'btn-loader disabled-without-opacity'}`}
                        />
                    </PopupButtons>
                </JsonPanel>
            </div>
        </OutsideClickPanel>
    </LeftDebugChatPanel>;
}

function mapStateToProps({ codeMetaData, clientCodeRunner, projectsInfo }: IStore, ownProps: CallProjectApiPanelOwnProps) {
    return {
        projectApis: codeMetaData.projectApis,
        paramsHistory: clientCodeRunner.paramsHistory[ownProps.projectId] || [],
        isProjectConnected: projectsInfo.currentProject.isProjectConnected
    };
}

function mapDispatchToProps(dispatch) {
    return {
        rememberProjectApiArguments(param: IRememberProjectApiArgumentsParams) {
            asyncDispatch(rememberProjectApiArguments, dispatch, param);
        },
        rememberProjectApiResult(param: IRememberProjectApiResultParams) {
            asyncDispatch(rememberProjectApiResult, dispatch, param);
        },
        getProjectApiHistory(param: IGetProjectApiHistoryParams): Promise<any> {
            return asyncDispatch(getProjectApiHistory, dispatch, param);
        },
        storeProjectApiHistory(param: IStoreProjectApiHistoryParams) {
            asyncDispatch(storeProjectApiHistory, dispatch, param);
        },
    };
}

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(CallProjectApiPanel);
